import Vue from 'vue'
import decode from 'jwt-decode'
import { getUser, getRefreshToken, getAccessToken, setUser, setAccessToken, setRefreshToken, clearCredentials } from '@/utils/cookie'
import { EventBus } from '@/router/bus'
import i18n from '@/i18n'
// import Sha256 from 'crypto-js/hmac-sha256'

// Claims
const CLAIMS = [
  'Claim_User_Create', // 0
  'Claim_User_Review', // 1
  'Claim_User_Revoke', // 2
  'Claim_Local_User_Create', // 3
  'Claim_Local_User_Review', // 4
  'Claim_Local_User_Revoke', // 5
  'Claim_Invoice_Create', // 6
  'Claim_Invoice_Cancel', // 7
  'Claim_Invoice_Review', // 8
  'Claim_Invoice_Settle', // 9
  'Claim_Invoice_Export', // 10
  'Claim_Notification_Create', // 11
  'Claim_Invoice_Assign', // 12
  'Claim_Registration_Edit', // 13
  'Claim_Invoice_Review_All', // 14
  'Claim_Invoice_Cancel_All', // 15
  'Claim_Invoice_Activate', // 16
  'Claim_Invoice_Activate_Once', // 17
  'Claim_Issue_Resolve', // 18
  'Claim_Group_Settlement_Create', // 19
  'Claim_Invoice_User_Settle' // 20
]

const ADMIN_CLAIM = 'Claim_Admin'

// Public methods

export function hasPermissionChangeAuthority (claims) {
  // Allow always for admin
  if (hasAdminClaim()) { return true }

  // Allow operators to change all users - except admins
  if (hasUserCreateClaim()) {
    if (claims.constructor === Array) {
      if (claims.indexOf(ADMIN_CLAIM) >= 0) {
        return false
      }
    } else if (claims.localeCompare(ADMIN_CLAIM) === 0) {
      return false
    }

    return true
  }

  // Allow local admin to change users - except admins and operators
  if (hasLocalUserCreateClaim()) {
    if (claims.constructor === Array) {
      if (claims.indexOf(ADMIN_CLAIM) >= 0 || claims.indexOf(CLAIMS[0]) >= 0) {
        return false
      }
    } else if (claims.localeCompare(ADMIN_CLAIM) === 0 || claims.localeCompare(CLAIMS[0]) === 0) {
      return false
    }

    return true
  }

  return false
}

export function getOrganizationId () {
  return getTokenOrganizationId()
}

export function getClaims () {
  return CLAIMS
}

export function getUserClaims () {
  return getTokenClaims()
}

export function isUserActive () {
  return isAuth() && accessTokenValid()
}

// TODO Check this if it's valid
export function isAuth () {
  return !!getUser()
}

export function hasAdminClaim () {
  return hasClaim(ADMIN_CLAIM)
}

export function hasUserCreateClaim () {
  return hasClaim(CLAIMS[0])
}

export function hasUserResetPasswordClaim () {
  return hasClaim(CLAIMS[1])
}

export function hasLocalUserCreateClaim () {
  return hasClaim(CLAIMS[3])
}

export function hasInvoiceCreateClaim () {
  return hasClaim(CLAIMS[6])
}

export function hasInvoiceReviewClaim () {
  return hasClaim(CLAIMS[8])
}

export function hasInvoiceCancelClaim () {
  return hasClaim(CLAIMS[7])
}

export function hasInvoiceExportClaim () {
  return hasClaim(CLAIMS[10])
}

export function hasNotificationCreateClaim () {
  return hasClaim(CLAIMS[11])
}

export function hasInvoiceAssignClaim () {
  return hasClaim(CLAIMS[12])
}

export function hasRegistrationEditClaim () {
  return hasClaim(CLAIMS[13])
}

export function hasInvoiceReviewAllClaim () {
  return hasClaim(CLAIMS[14])
}

export function hasInvoiceCancelAllClaim () {
  return hasClaim(CLAIMS[15])
}

export function hasInvoiceActivateClaim () {
  return hasClaim(CLAIMS[16])
}

export function hasInvoiceActivateOnceClaim () {
  return hasClaim(CLAIMS[17])
}

export function hasIssueResolveClaim () {
  return hasClaim(CLAIMS[18])
}

export function hasGroupSettlementCreateClaim () {
  return hasClaim(CLAIMS[19])
}

export function hasInvoiceUserSettlementClaim () {
  return hasClaim(CLAIMS[20])
}

export function getPermissionClaims () {
  return getTokenClaims()
}

export function accessTokenValid () {
  var token = getAccessToken()
  return token && !isTokenExpired(token)
}

export function refreshTokenValid () {
  var token = getRefreshToken()
  return token && !isTokenExpired(token)
}

export function setCredentials (user, accessToken, refreshToken) {
  setUser(user)
  setAccessToken(accessToken)
  setRefreshToken(refreshToken)

  // let authorizationKey = Sha256(user, Vue.$config.secret_key)
  // Vue.http.headers.common['From'] = authorizationKey.toString()
  // console.log(Vue.http.headers)
}

export function logout () {
  clearCredentials()
  // Vue.http.headers.common['From'] = ''
}

export function refreshToken (to, from, next) {
  var refreshTokenUrl = Vue.$apiConfig.loginRefresh
  let isProductionEnvironment = process.env.NODE_ENV.toUpperCase() === 'production'.toUpperCase()

  if (!isProductionEnvironment) { console.log('Access token expired - sending refresh access token request from router') }

  Vue.http.headers.common['Authorization'] = 'Bearer ' + getRefreshToken()

  Vue.http.get(refreshTokenUrl)
    .then(response => {
      if (!isProductionEnvironment) { console.log('Access token obtained successfully - redirecting to ' + to.path) }
      setAccessToken(response.data.payload.accessToken)
      Vue.http.headers.common['Authorization'] = 'Bearer ' + getAccessToken()
      next({
        path: to.path
      })
    }, error => {
      if (!isProductionEnvironment) { console.log(error) }

      if (error.status === 0) {
        EventBus.$emit('alert', {
          variant: 'danger',
          message: i18n.t('apiStatusCodes.0'),
          errors: []
        })
      }

      next({
        path: '/'
      })
    })
}

// Private methods

function hasClaim (claim) {
  let claims = getTokenClaims()
  if (claims.constructor === Array) {
    return claims.indexOf(claim) >= 0 || claims.indexOf(ADMIN_CLAIM) >= 0
  }

  return claims.localeCompare(claim) === 0 || claims.localeCompare(ADMIN_CLAIM) === 0
}

function getTokenClaims () {
  var token = getAccessToken()
  var decodedToken = decode(token)

  return decodedToken.cir_claims
}

function getTokenOrganizationId () {
  var token = getAccessToken()
  var decodedToken = decode(token)

  return decodedToken.groupsid
}

function getTokenExpirationDate (encodedToken) {
  var token = decode(encodedToken)
  if (!token.exp) { return null }

  var date = new Date(0)
  date.setUTCSeconds(token.exp)

  return date
}

function isTokenExpired (token) {
  var expirationDate = getTokenExpirationDate(token)
  return expirationDate < new Date()
}



// WEBPACK FOOTER //
// ./src/router/auth.js